import type { Recording } from "src/types";

import { hasDisplayDescriptionContent } from "./has-display-description-content";

/** Same formatting as {@link buildRecordingTitle} in recordings helpers (inlined to avoid editor import chain in tests). */
function formatRecordingTitleFromStartedAt(startedAt: string): string {
  try {
    const date = new Date(startedAt);
    return new Intl.DateTimeFormat("en", {
      dateStyle: "long",
      timeStyle: "short",
      hour12: true,
    }).format(date);
  } catch {
    return "";
  }
}

/** Optional display overrides on a Video node or grid `videoList` recording item. */
export type RecordingDisplayMetadataFields = {
  displayTitle?: string | null;
  displayDescription?: string | null;
  displayThumbnail?: string | null;
};

/** True when any display override is stored on the block (not merely recording defaults). */
export function hasCustomRecordingDisplayOverrides(
  metadata: RecordingDisplayMetadataFields
): boolean {
  return (
    metadata.displayTitle !== undefined ||
    metadata.displayDescription !== undefined ||
    metadata.displayThumbnail !== undefined
  );
}

/**
 * Description for public view: custom HTML when it has visible text; otherwise the
 * recording API description. An empty `displayDescription` (e.g. cleared in Details)
 * does not block the recording fallback.
 */
export function resolveVideoDescriptionHtml(
  recording: Recording | undefined,
  metadata: RecordingDisplayMetadataFields
): string {
  const custom = metadata.displayDescription;
  if (hasDisplayDescriptionContent(custom)) {
    return custom!.trim();
  }
  if (recording && hasDisplayDescriptionContent(recording.description)) {
    return recording.description;
  }
  return "";
}

/**
 * Title for canvas / preview / published views. Custom title wins when set; otherwise
 * API title; when both are empty, formats {@link Recording.startedAt} (same as library).
 * An explicit empty `displayTitle` stays blank (intentional override).
 */
export function resolveRecordingDisplayTitle(
  recording: Recording,
  metadata: RecordingDisplayMetadataFields
): string {
  if (metadata.displayTitle === "") {
    return "";
  }

  const merged = metadata.displayTitle ?? recording.title;
  const trimmed = (merged ?? "").trim();
  if (trimmed) {
    return trimmed;
  }

  return formatRecordingTitleFromStartedAt(
    recording.startedAt || recording.createdAt
  );
}

/**
 * Merges API recording data with block metadata. Title/thumbnail: metadata wins when
 * non-nullish (`??`); empty string is an intentional override. Description uses
 * {@link resolveVideoDescriptionHtml}.
 */
export function effectiveRecordingDisplay(
  recording: Recording,
  metadata: RecordingDisplayMetadataFields
) {
  return {
    title: resolveRecordingDisplayTitle(recording, metadata),
    description: resolveVideoDescriptionHtml(recording, metadata),
    thumbnail: (metadata.displayThumbnail ?? recording.thumbnail) || "",
  };
}

type VideoBlockSourceFields = {
  url?: string | null;
  recordingId?: string | null;
  title?: string;
  thumbnail?: string;
} & RecordingDisplayMetadataFields;

/**
 * Single Video: user picked a different library recording — clear embed fields and
 * drop display overrides so title/description/thumbnail come from the recording
 * (shown as placeholders in Details; merged on canvas via `effectiveRecordingDisplay`).
 */
export function applyLibraryRecordingToVideoBlock(
  props: VideoBlockSourceFields,
  recordingId: string
) {
  if (!recordingId) return;

  props.recordingId = recordingId;
  props.url = null;
  props.title = undefined;
  props.thumbnail = undefined;
  props.displayTitle = undefined;
  props.displayDescription = undefined;
  props.displayThumbnail = undefined;
}
